import { Box, Flex, Text } from '@chakra-ui/react';
import { FormFeedback, OutlineCard } from '@morressier/alembic';
import { ISubmissionPreference } from '@publishing-engine/domain/models';
import { FormFieldDataAvailability, FormFieldDateInput, FormFieldRadio, FormFieldTextInput } from '@publishing-engine/domain/types/article';
import { get } from 'lodash';
import { Control, FieldErrors, UseFormWatch } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { CustomFormFieldComponent } from '../../FormField';
import RadioGroup from '../../../pages/Organization/public/Journal/SubmissionUpload/components/common/RadioGroup';
import { RadioGroup as RadioGroupCommon } from '../../RadioGroup';
import ConditionalWrapper from '../../ConditionalWrapper';


export type IDataAvailabilityForm = {
  preference: {
    dataAvailability: ISubmissionPreference['dataAvailability'];
  };
};

interface Props {
  control: Control<IDataAvailabilityForm, any>;
  watch: UseFormWatch<IDataAvailabilityForm>;
  errors: FieldErrors<IDataAvailabilityForm>;
  config: FormFieldDataAvailability;
  DataAvailabilityOpenlyAvailableUrlConfig?: FormFieldTextInput<'preference.dataAvailabilityOpenlyAvailableUrl'>;
  DataAvailabilityEmbargoUrlConfig?: FormFieldTextInput<'preference.dataAvailabilityEmbargoUrl'>;
  DataAvailabilityEmbargoDateConfig?: FormFieldDateInput<'preference.dataAvailabilityEmbargoDate'>;
  DataUnavailabilityReasonConfig?: FormFieldRadio<'preference.dataUnavailabilityReason'>;

}

const DataAvailabilityFormItem: React.FC<Props> = ({
  config,
  control,
  errors,
  watch,
  DataAvailabilityOpenlyAvailableUrlConfig,
  DataAvailabilityEmbargoUrlConfig,
  DataAvailabilityEmbargoDateConfig,
  DataUnavailabilityReasonConfig

}) => {
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;
  const mainField = `${field}.status` as const;

  const selectedValue = watch(mainField);
  const valueError = get(errors, mainField);
  const { submissionContext } = useSubmissionContextData();
  const dataAvailability =
    submissionContext?.config.cms.authorSubmissionForm.submissionPreferences
      .dataAvailability; 
      
if (DataUnavailabilityReasonConfig) {
  const dependentOptions = config.params.labels.dependentOptions;
  const orderedKeys = Object.keys(dependentOptions);

  DataUnavailabilityReasonConfig.params.validations.options = orderedKeys;

  const orderedOptions: Record<string, { main: string; secondary: string }> = {};

  for (const key of orderedKeys) {
    const value = dependentOptions[key];

    orderedOptions[key] = {
      main: key,
      secondary: value?.main || ""
    };
  }

  DataUnavailabilityReasonConfig.params.labels.options = orderedOptions;
}
  return (
    <Flex flexDirection="column" gap={4}>
      <Box>
        <Text textStyle="headingGroup">
          {dataAvailability?.mainLabel ?? 'Data Availability'}
        </Text>
        <Text>{dataAvailability?.secondaryLabel}</Text>
      </Box>

      <OutlineCard borderless width="100%" padding={4}>
        {/* ----- Data Availability ----- */}
        <Flex flexDirection="column" gap={4} width="100%">
          <FieldLabel
            label={labels.main}
            subText={labels.subtitle}
            isRequired={validations.required}
            helpText={labels.helpText}
          />

          <RadioGroupCommon
            control={control}
            name={mainField}
            options={Object.keys(config.params.labels.options).map(option => ({
              value: option,
              children: (
                <Text>{config.params.labels.options[option]?.main}</Text>
              )
            }))}
          />

          {valueError ? (
            <FormFeedback variant="error">
              Indicate your preference for data availability before proceeding.
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>

      <ConditionalWrapper
        condition={
          selectedValue === 'openly-available' ||
          selectedValue === 'embargo-period' ||
          selectedValue === 'not-available'
        }
        wrapper={children => (
          <OutlineCard borderless width="100%" padding={4}>
            <Flex flexDirection="column" gap={4} width="100%">
              {children}
            </Flex>
          </OutlineCard>
        )}
      >
        {DataAvailabilityOpenlyAvailableUrlConfig && (
          <CustomFormFieldComponent
            config={DataAvailabilityOpenlyAvailableUrlConfig}
            placeholder="Enter DOI here"
          />
        )}

        {DataAvailabilityEmbargoUrlConfig && (
          <CustomFormFieldComponent
            config={DataAvailabilityEmbargoUrlConfig}
            placeholder="Enter DOI here"
          />
        )}
        {DataAvailabilityEmbargoDateConfig && (
          <CustomFormFieldComponent
            config={DataAvailabilityEmbargoDateConfig}
            placeholder="Enter Embargo Date"
          />
        )}
        {DataUnavailabilityReasonConfig && (
          <RadioGroup config={DataUnavailabilityReasonConfig} />
        )}
      </ConditionalWrapper>
    </Flex>
  );
};

export default DataAvailabilityFormItem;
